import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { lockScroll } from '../lib/scrollLock';

/** Far enough down that it was a dismissal rather than a stray touch. */
const DISMISS_PX = 90;
/** Or a flick: fast enough that where it got to matters less than how quickly. */
const FLICK_PX_PER_MS = 0.5;
const FLICK_MIN_PX = 30;

/**
 * Bottom sheet. Thumb-reachable on a propped-up phone; Escape closes it.
 *
 * Rendered through a portal to the body, which is not a detail. `position:
 * fixed` is only fixed to the viewport while no ancestor has a transform —
 * any transform, including one applied by an animation, makes that ancestor the
 * containing block instead. The standings slider animates a wrapper around the
 * whole list, so a sheet opened from a row inside it stopped covering the
 * screen and rendered inline at the top of the column instead. The portal puts
 * every sheet outside that risk permanently rather than one wrapper at a time.
 */
export function Sheet({
  title,
  label,
  onClose,
  children,
}: {
  title?: string;
  /** Names the dialog for screen readers when the content supplies its own heading. */
  label?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const sheet = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const unlock = lockScroll();
    return () => {
      window.removeEventListener('keydown', onKey);
      unlock();
    };
  }, [onClose]);

  /**
   * Swipe it away.
   *
   * There has been a grip drawn at the top of every sheet since the beginning
   * and nothing behind it — a handle is a promise that the thing can be
   * dragged, and the only way out was tapping the strip of scrim above it,
   * which on a tall sheet is a long reach and on a full one barely exists.
   *
   * Only armed at the top of the sheet's own scroll. Anywhere else a downward
   * drag is somebody reading, and a sheet that closes when you try to scroll
   * back up is worse than one that does not close at all. `preventDefault`
   * during the drag stops the scroll fighting the gesture, which is why the
   * listener has to be non-passive and therefore attached by hand.
   */
  useEffect(() => {
    const el = sheet.current;
    if (!el) return;

    let dragging = false;
    let startY = 0;
    let startedAt = 0;
    let travelled = 0;

    const settle = () => {
      el.style.transition = '';
      el.style.transform = '';
    };

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1 || el.scrollTop > 0) return;
      dragging = true;
      startY = e.touches[0]!.clientY;
      startedAt = Date.now();
      travelled = 0;
    };

    const onMove = (e: TouchEvent) => {
      if (!dragging) return;
      travelled = e.touches[0]!.clientY - startY;
      if (travelled <= 0) {
        // Heading back up. Hand the gesture to the scroller and stay out of it.
        travelled = 0;
        settle();
        return;
      }
      e.preventDefault();
      el.style.transition = 'none';
      el.style.transform = `translateY(${travelled}px)`;
    };

    const onEnd = () => {
      if (!dragging) return;
      dragging = false;
      const speed = travelled / Math.max(1, Date.now() - startedAt);
      if (travelled > DISMISS_PX || (speed > FLICK_PX_PER_MS && travelled > FLICK_MIN_PX)) {
        onClose();
        return;
      }
      settle();
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="scrim"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={sheet}
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title ?? label}
      >
        <div className="sheet-grip" />
        {title && <h2>{title}</h2>}
        {children}
      </div>
    </div>,
    document.body,
  );
}
